import { track } from '../lib/funnel';
import { useT } from '../i18n/locale-context';
import { COMPANY, HAS_TELEGRAM_CHANNEL } from '../lib/company';

type Surface = 'tenderDetail' | 'catalog' | 'market';

/**
 * Card CTA linking to the public Telegram content channel (auto-posted
 * large/notable tenders) — distinct from `ConnectTelegramBanner`, which
 * connects the LOGIN widget for saved-search alerts. Mounted per-surface by
 * the caller; fires `channel_subscribe_clicked` with the mounting surface on
 * click. Mirrors `connect-telegram-banner.tsx`'s Tailwind tokens.
 */
export function ChannelSubscribeCta({ surface }: { surface: Surface }) {
  const t = useT();
  // KZ has a bot but no channel — this CTA exists only to point at a channel,
  // so it renders nothing there. `useT` above is the only hook this component
  // calls, so returning before render doesn't change hook order.
  if (!HAS_TELEGRAM_CHANNEL) return null;
  // On the tender detail page this mounts flush under `SimilarTenders` with no
  // gap; add top spacing there only. Catalog/market place it inside a stack that
  // already spaces it, so leave those untouched.
  const spacing = surface === 'tenderDetail' ? 'mt-6 mb-4' : 'mb-4';
  return (
    <div className={`relative ${spacing} rounded-lg border border-brand/30 bg-brand-dim p-4`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[13.5px] font-semibold text-ink">{t('channelCta.title')}</p>
          <p className="text-[12.5px] text-ink-soft">{t('channelCta.desc')}</p>
        </div>
        <a
          href={COMPANY.channelUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => track('channel_subscribe_clicked', { surface })}
          className="shrink-0 rounded-md bg-brand px-3 py-1.5 text-[12.5px] font-semibold text-white"
        >
          {t('channelCta.button')}
        </a>
      </div>
    </div>
  );
}
